/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';

import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  tableCellClasses,
} from '@mui/material';
import { DateTime } from 'luxon';

import { IRenderedSTOItem, ISTOSuratPesananMappedData } from './interfaces';
import S from './STOSuratPesananPrintTemplate.styles';

interface STOSuratPesananPrintTemplateComponentProps {
  data: ISTOSuratPesananMappedData;
}

const getMaxPageNumber = (tableData: IRenderedSTOItem[][]): number => {
  const additionalPage =
    (tableData.length > 1 && tableData[tableData.length - 1].length > 17) ||
    (tableData.length === 1 && tableData[tableData.length - 1].length > 9)
      ? 1
      : 0;

  return tableData.length + additionalPage;
};

export const STOSuratPesananPrintTemplateComponent = ({
  data,
}: STOSuratPesananPrintTemplateComponentProps): React.JSX.Element => {
  const [maxPage, setMaxPage] = useState<number>(0);
  const renderHeader = () => (
    <S.PageHeader>
      <S.HeaderCopeWrapper>
        <S.SuratPesananLabel>SURAT PESANAN</S.SuratPesananLabel>
      </S.HeaderCopeWrapper>
      {/* Surat Pesanan Cope Section */}
      <S.STOSuratPesananCopeWrapper container>
        <Grid item xs={3}>
          <img
            src="/assets/print-template/TMPLogo.png"
            alt="logo"
            style={{
              width: '120%',
              marginTop: '12px',
              marginLeft: '-40px',
            }}
          />
        </Grid>
        {/* Section Company Profile */}
        <S.CompanyProfileWrapper item xs={9}>
          <div>{data.to_warehouse_info?.name}</div>
          <S.CompanyAddressText>
            {data.to_warehouse_info?.address}
          </S.CompanyAddressText>
          <div>No Izin PBF: {data.to_warehouse_info?.pbf_no}</div>
          <div>No Izin DAK: {data.to_warehouse_info?.dak_no}</div>
          <div>No Sertifikat CDOB : {data.to_warehouse_info?.cdob_no}</div>
          <div>No Sertifikat CDAKB : {data.to_warehouse_info?.cdakb_no}</div>
          {data.to_warehouse_info?.cdob_ccp_no && (
            <div>
              No Sertifikat CDOB CCP : {data.to_warehouse_info?.cdob_ccp_no}
            </div>
          )}
        </S.CompanyProfileWrapper>
      </S.STOSuratPesananCopeWrapper>

      <S.STOSuratPesananCopeWrapper container>
        {/* Left Section Nomor TO etc */}
        <Grid item xs={6} style={{ paddingTop: 4 }}>
          <Grid container>
            <Grid item xs={3}>
              Nomor TO
            </Grid>
            <Grid item xs={1}>
              :
            </Grid>
            <Grid item xs={8}>
              {data.sto_number}
            </Grid>
            <Grid item xs={3}>
              Dikirimkan dari
            </Grid>
            <Grid item xs={1}>
              :
            </Grid>
            <Grid item xs={8}>
              {data.from_warehouse_info?.address}
            </Grid>
          </Grid>
        </Grid>
        {/* Right Section Tanggal TO etc */}
        <Grid item xs={6} style={{ paddingTop: 4 }}>
          <Grid container>
            <Grid item xs={3}>
              Tanggal TO
            </Grid>
            <Grid item xs={1}>
              :
            </Grid>
            <Grid item xs={8}>
              {data.createdAt}
            </Grid>
            <Grid item xs={3}>
              Dikirimkan ke
            </Grid>
            <Grid item xs={1}>
              :
            </Grid>
            <Grid item xs={8}>
              {data.to_warehouse_info?.address}
            </Grid>
          </Grid>
        </Grid>
      </S.STOSuratPesananCopeWrapper>

      {/* Customer Info Section */}
      <S.PrintInfoWrapper container>
        <Grid container item xs={2}>
          <Grid item xs={11}>
            Dicetak pada
          </Grid>
          <Grid item xs={1}>
            :
          </Grid>
        </Grid>
        <Grid item xs={10}>
          {DateTime.fromJSDate(new Date()).toFormat('dd/MM/yyyy')}
        </Grid>
        <Grid container item xs={2}>
          <Grid item xs={11}>
            Total halaman
          </Grid>
          <Grid item xs={1}>
            :
          </Grid>
        </Grid>
        <Grid item xs={10}>
          {maxPage}
        </Grid>
      </S.PrintInfoWrapper>
    </S.PageHeader>
  );

  useEffect(() => {
    setMaxPage(getMaxPageNumber(data.items));
  }, [maxPage, data.items]);

  // Footer Section
  const renderFooter = () =>
    Array.from(Array(maxPage).keys()).map((idx) => (
      <S.PageFooter className="last-page" pageOffset={idx}>
        <Grid container>
          <Grid item xs={12}>
            <S.PageFooterText>
              Unless otherwise specified, the mentioned Purchase Order is
              subject to the following terms and conditions:
            </S.PageFooterText>
          </Grid>
          <Grid item xs={10}>
            <S.PageFooterText>
              (1). Purchase Order will be expired in 14 days.
            </S.PageFooterText>
          </Grid>
          <Grid item xs={2} style={{ textAlign: 'right' }}>
            <S.PageNumberText>
              Page {idx + 1} of {maxPage}
            </S.PageNumberText>
          </Grid>
        </Grid>
      </S.PageFooter>
    ));

  const renderBody = (
    idx: number,
    itemData: IRenderedSTOItem[],
    isLastPage: boolean,
  ) => (
    <S.PageBody>
      <TableContainer style={{ marginTop: idx === 0 ? '0' : '8mm' }}>
        <Table
          sx={{
            minWidth: 650,
            [`& .${tableCellClasses.root}`]: {
              borderBottom: '1px solid black',
              fontFamily: 'Arial',
              fontSize: 12,
            },
          }}
          size="small"
          aria-label="a dense table"
        >
          <S.TableHeadStyle>
            <TableRow>
              <S.TableHeaderCell width={75}>SKU</S.TableHeaderCell>
              <S.TableHeaderCell width={220}>NAMA PRODUK</S.TableHeaderCell>
              <S.TableHeaderCell width={65}>KEMASAN</S.TableHeaderCell>
              <S.TableHeaderCell width={60}>QTY</S.TableHeaderCell>
            </TableRow>
          </S.TableHeadStyle>
          <TableBody>
            {itemData.map((productInfo, idx) => (
              <S.TableRowStyle
                key={`STOSuratPesananRow-${productInfo.sku_code}-${idx}`}
              >
                <S.TableBodyCell>{productInfo.sku_code}</S.TableBodyCell>
                <S.TableBodyCell>{productInfo.name}</S.TableBodyCell>
                <S.TableBodyCell>{productInfo.uom}</S.TableBodyCell>
                <S.TableBodyCell>{productInfo.quantity}</S.TableBodyCell>
              </S.TableRowStyle>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* If not last page  */}
      {!isLastPage ? (
        <>
          <div className="pagebreak-after" />
          <div style={{ width: '100%' }} />
        </>
      ) : (
        <>
          {/* Additional page break if maxPage > data.items.length
           because rest of page will not enough to render summary section. Signature section will 
           be rendered separately in the next page */}
          {maxPage > data.items.length && (
            <>
              <div className="pagebreak-after" />
              <div style={{ width: '100%' }} />
            </>
          )}
          <Grid container>
            {/* Signature Section */}
            <S.SignatureSectionWrapper item xs={5}>
              <Grid container>
                <Grid item xs={4} style={{ display: 'flex' }}>
                  <p style={{ alignSelf: 'flex-end' }}>Diverifikasi oleh,</p>
                </Grid>
                <Grid item xs={4}>
                  <img
                    id="inv-sign"
                    style={{ width: 120 }}
                    src={data.to_warehouse_info?.apj_signature}
                    alt="ttd"
                  />
                </Grid>
                <Grid item xs={12}>
                  {data.to_warehouse_info?.apj_title}
                </Grid>
                <Grid item xs={12}>
                  {data.to_warehouse_info?.apj_name}
                </Grid>
                <Grid item xs={12}>
                  {data.to_warehouse_info?.apj_license_label} :{' '}
                  {data.to_warehouse_info?.apj_sipa}
                </Grid>
              </Grid>
            </S.SignatureSectionWrapper>
          </Grid>
        </>
      )}
    </S.PageBody>
  );

  return (
    <div>
      <S.PageWrapper>
        <TableContainer>
          <Table>
            {/* Header Section */}
            {renderHeader()}

            {/* Content Section */}
            {data.items.map((itemData, idx) =>
              renderBody(idx, itemData, data.items.length === idx + 1),
            )}

            {/* Footer Section */}
            {renderFooter()}
          </Table>
        </TableContainer>
      </S.PageWrapper>
    </div>
  );
};
